/**
 * Passiver Verbindungsstatus für alle Module.
 *
 * Ein axios-Interceptor (an `api` aus client.ts gehängt) meldet jede Antwort an einen Store:
 *  - erreichbar (Antwort mit Status < 500 oder 4xx) -> "letzte erfolgreiche Antwort" aktualisieren, Fehlerzähler zurücksetzen
 *  - Verbindungsfehler (keine Antwort, Netzwerkfehler, Timeout, 502/503/504) -> Fehlerzähler erhöhen
 *  - übrige 5xx (z. B. 500) werden als `lastError` vermerkt, zählen aber NICHT als "Verbindung gestört"
 *    (der Server ist ja erreichbar; ein Programmfehler an einem Endpunkt darf keinen Alarm auslösen)
 *  - 4xx (401/403/404/422 ...) = Server erreichbar -> zählt als Lebenszeichen, nie als Fehler
 *  - Langläufer (DJI, Medien, Storage, Timeout >= 15 s) und Anfragen mit `skipConnection: true`:
 *    Erfolg zählt als Lebenszeichen, ein Fehler wird NICHT gewertet
 *  - 401-Refresh-Retry (client.ts) bleibt unsichtbar: der Store sieht nur das Endergebnis
 *
 * Das Modul hat keine React-Abhängigkeit; die UI nutzt `subscribeConnection` / `getConnectionState`.
 */
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

export type ConnectionStatus = 'unknown' | 'ok' | 'degraded' | 'offline'
export type ConnectionErrorKind = 'network' | 'timeout' | 'gateway' | 'server'

export interface ConnectionError {
  at: number
  kind: ConnectionErrorKind
  message: string
  status?: number
  url?: string
}

export interface ConnectionState {
  /** unknown = noch keine Antwort; ok; degraded = Fehler seit der letzten Antwort; offline = ab `failThreshold` Fehlern in Folge oder Browser offline */
  status: ConnectionStatus
  /** Zeitpunkt (ms) der letzten erreichbaren Antwort, sonst null */
  lastSuccessAt: number | null
  lastError: ConnectionError | null
  /** gezählte Verbindungsfehler seit der letzten erreichbaren Antwort */
  failCount: number
  /** laufende Anfragen */
  pending: number
  /** navigator.onLine */
  online: boolean
}

export interface ConnectionOptions {
  /** Fehler in Folge, ab denen der Status "offline" (rot) ist. Standard 2. */
  failThreshold?: number
  /** URL-Teile von Langläufern, deren Fehler ignoriert werden. Standard: /dji/, /media/, /storage/, /wayline */
  ignoreUrls?: string[]
  /** Timeout (ms) ab dem eine Anfrage als Langläufer gilt. Standard 15000. */
  longRunningMs?: number
}

const DEFAULTS: Required<ConnectionOptions> = {
  failThreshold: 2,
  ignoreUrls: ['/dji/', '/media/', '/storage/', '/wayline'],
  longRunningMs: 15000,
}
let options: Required<ConnectionOptions> = { ...DEFAULTS }

const initialOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false)

let state: ConnectionState = {
  status: 'unknown', lastSuccessAt: null, lastError: null, failCount: 0, pending: 0, online: initialOnline(),
}
const listeners = new Set<() => void>()
const retryListeners = new Set<() => void>()

function derive(s: Omit<ConnectionState, 'status'>): ConnectionStatus {
  if (!s.online || s.failCount >= options.failThreshold) return 'offline'
  if (s.failCount > 0) return 'degraded'
  return s.lastSuccessAt === null ? 'unknown' : 'ok'
}

function set(patch: Partial<Omit<ConnectionState, 'status'>>) {
  const next = { ...state, ...patch }
  next.status = derive(next)
  // keine Benachrichtigung, wenn sich nichts geändert hat (Polling im Sekundentakt)
  const same = (Object.keys(next) as (keyof ConnectionState)[]).every(k => next[k] === state[k])
  state = next
  if (!same) listeners.forEach(l => l())
}

export function configureConnection(opts: ConnectionOptions) {
  options = { ...options, ...opts }
  set({})
}

export function getConnectionState(): ConnectionState {
  return state
}

export function subscribeConnection(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Manuelle Meldung für Nicht-axios-Aufrufe (fetch): erreichbare Antwort. */
export function reportConnectionSuccess() {
  set({ lastSuccessAt: Date.now(), failCount: 0 })
}

/** Manuelle Meldung für Nicht-axios-Aufrufe (fetch): Verbindungsfehler. */
export function reportConnectionError(message: string, opts?: { kind?: ConnectionErrorKind; status?: number; url?: string }) {
  const kind = opts?.kind ?? 'network'
  const error: ConnectionError = { at: Date.now(), kind, message, status: opts?.status, url: opts?.url }
  set({ lastError: error, failCount: kind === 'server' ? state.failCount : state.failCount + 1 })
}

/**
 * "Erneut versuchen": benachrichtigt alle Abonnenten (Seiten laden neu) und löst einen Ping aus.
 * Seiten registrieren sich mit `onConnectionRetry(() => laden())` bzw. dem React-Hook useConnection().onRetry.
 */
export function requestConnectionRetry() {
  retryListeners.forEach(l => { try { l() } catch { /* Seite darf den Retry nicht verhindern */ } })
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('connection:retry'))
  void pingNow()
}
export function onConnectionRetry(cb: () => void): () => void {
  retryListeners.add(cb)
  return () => { retryListeners.delete(cb) }
}

// ---------------------------------------------------------------------------
// axios-Anbindung
// ---------------------------------------------------------------------------
type Tracked = InternalAxiosRequestConfig & { skipConnection?: boolean; __connPending?: boolean }

function isLongRunning(config?: Tracked): boolean {
  if (!config) return false
  if (config.skipConnection) return true
  if ((config.timeout ?? 0) >= options.longRunningMs) return true
  const url = String(config.url ?? '')
  return options.ignoreUrls.some(p => url.includes(p))
}

function finishPending(config?: Tracked) {
  if (config?.__connPending) {
    config.__connPending = false
    set({ pending: Math.max(0, state.pending - 1) })
  }
}

const installed = new WeakSet<object>()

/** Hängt den Status-Interceptor an `api` (idempotent). Muss NACH dem Refresh-Interceptor registriert werden. */
export function installConnectionInterceptor(api: AxiosInstance, opts?: ConnectionOptions) {
  if (opts) options = { ...options, ...opts }
  if (installed.has(api)) return
  installed.add(api)

  api.interceptors.request.use(config => {
    const c = config as Tracked
    if (!c.__connPending) { c.__connPending = true; set({ pending: state.pending + 1 }) }
    return config
  })

  api.interceptors.response.use(
    res => {
      // Der Refresh-Interceptor kann nach fehlgeschlagenem Refresh `undefined` liefern
      if (res) {
        finishPending((res as any).config)
        reportConnectionSuccess()
      }
      return res
    },
    err => {
      const config = err?.config as Tracked | undefined
      finishPending(config)
      const status: number | undefined = err?.response?.status
      if (status !== undefined && status < 500) {
        // 4xx: Server antwortet -> erreichbar (das Ergebnis selbst behandelt die Seite)
        reportConnectionSuccess()
      } else if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        // vom Aufrufer abgebrochen: kein Signal
      } else if (!isLongRunning(config)) {
        const url = config?.url
        if (status === undefined) {
          const timeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT'
          reportConnectionError(timeout ? 'Zeitüberschreitung' : 'Server nicht erreichbar', { kind: timeout ? 'timeout' : 'network', url })
        } else if (status === 502 || status === 503 || status === 504) {
          reportConnectionError(`Server nicht verfügbar (${status})`, { kind: 'gateway', status, url })
        } else {
          reportConnectionError(`Serverfehler (${status})`, { kind: 'server', status, url })
        }
      }
      return Promise.reject(err)
    },
  )
}

// ---------------------------------------------------------------------------
// Browser online/offline
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { set({ online: true }); void pingNow() })
  window.addEventListener('offline', () => set({ online: false }))
}

// ---------------------------------------------------------------------------
// Optionaler Ping (Module ohne Polling, z. B. Admin)
// ---------------------------------------------------------------------------
let pingFn: (() => Promise<unknown>) | null = null
let pingTimer: ReturnType<typeof setInterval> | null = null

async function pingNow() {
  if (!pingFn) return
  try { await pingFn() } catch { /* Interceptor hat den Fehler bereits gewertet */ }
}

/**
 * Startet einen leichten Ping (Standard: `GET /auth/me` über `api`), aber nur wenn seit `intervalMs / 2`
 * keine andere Antwort kam und der Tab sichtbar ist. Gibt die Stop-Funktion zurück.
 * `request` kann ersetzt werden (z. B. ein anderer leichter Endpunkt).
 */
export function startConnectionPing(api: AxiosInstance, opts?: { intervalMs?: number; request?: () => Promise<unknown> }): () => void {
  const intervalMs = opts?.intervalMs ?? 30000
  // Ohne Anmeldung (Login-Seite) kein Ping: /auth/me würde 401 liefern und den Refresh-Interceptor zum Redirect auf /login treiben
  const angemeldet = () => {
    try {
      const key = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
      return !!localStorage.getItem(key)
    } catch { return false }
  }
  pingFn = opts?.request ?? (() => (angemeldet() ? api.get('/auth/me') : Promise.resolve()))
  if (pingTimer) clearInterval(pingTimer)
  const tick = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    if (state.lastSuccessAt !== null && Date.now() - state.lastSuccessAt < intervalMs / 2 && state.failCount === 0) return
    void pingNow()
  }
  pingTimer = setInterval(tick, intervalMs)
  void pingNow()
  return () => {
    if (pingTimer) clearInterval(pingTimer)
    pingTimer = null
    pingFn = null
  }
}

/** Nur für Tests: Zustand und Optionen zurücksetzen. */
export function resetConnectionForTests() {
  options = { ...DEFAULTS }
  state = { status: 'unknown', lastSuccessAt: null, lastError: null, failCount: 0, pending: 0, online: true }
  listeners.forEach(l => l())
}
